import type { KeyboardEvent } from 'react';
import { FieldSize, Size, assertLengthCap, t, usePasswordInput, type PasswordPurpose, type PasswordStrengthStep, type StringKey } from '@bms2/shared';
import { Icon } from '../Icon';
import { FieldShell } from './FieldShell';
import styles from './field.module.css';

/**
 * Password Input (piece 3): the native password field with a reveal toggle
 * that starts off and is never remembered across fields, a caps-lock warning
 * while focused, autocomplete set per use, paste always allowed, no length cap
 * below 64, never a mask, and a strength meter on set and change only.
 */
export interface PasswordInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  purpose: PasswordPurpose;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
  name?: string;
  maxLength?: number;
  /** Which of the widths the box takes, Column in a line table's cell; a passphrase reads at Medium (decisions 0583, 0647). */
  size?: FieldSize;
}

export const passwordInputDocumentedStates = ['login', 'set', 'change'] as const;

const stepLabelKey: Record<PasswordStrengthStep, StringKey> = {
  length8: 'password.rule.length8',
  length14: 'password.rule.length14',
  twoKinds: 'password.rule.twoKinds',
  allKinds: 'password.rule.allKinds',
};

export function PasswordInput({ label, value, onChange, purpose, required, error, disabled, name, maxLength, size = Size.Medium }: PasswordInputProps) {
  assertLengthCap(maxLength);
  const pw = usePasswordInput(purpose, value);
  const readCapsLock = (e: KeyboardEvent<HTMLInputElement>) => pw.setCapsLock(e.getModifierState('CapsLock'));
  return (
    <FieldShell label={label} required={required ?? false} error={error ?? null} disabled={disabled ?? false} size={size}>
      {({ inputId, messageId }) => (
        <div className={styles.field} data-purpose={purpose}>
          <div className={styles.control}>
            <input
              id={inputId}
              className={styles.input}
              type={pw.revealed ? 'text' : 'password'}
              name={name}
              value={value}
              required={required}
              disabled={disabled}
              maxLength={maxLength}
              autoComplete={pw.autocomplete}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? messageId : undefined}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={readCapsLock}
              onKeyUp={readCapsLock}
              onFocus={() => pw.setFocused(true)}
              onBlur={() => pw.setFocused(false)}
            />
            <button
              type="button"
              className={styles.iconButton}
              aria-pressed={pw.revealed}
              aria-label={pw.revealed ? t('password.hide') : t('password.show')}
              data-reveal={pw.revealed ? 'on' : 'off'}
              onClick={pw.toggle}
              disabled={disabled}
            >
              <Icon meaning={pw.revealed ? 'hide' : 'reveal'} size="header" />
            </button>
          </div>
          {pw.showsCapsLockWarning ? (
            <p className={styles.status} role="status" data-caps-lock="on">
              {t('password.capsLock')}
            </p>
          ) : null}
          {pw.showsMeter ? (
            <>
              <meter className={styles.meter} min={0} max={4} low={2} high={3} optimum={4} value={pw.strength} aria-label={t('password.strength')} data-strength={pw.strength} />
              <ul className={styles.steps} aria-label={t('password.rules')} data-strength-steps="true">
                {pw.steps.map(({ step, met }) => (
                  <li key={step} className={styles.step} data-step={step} data-met={met ? 'true' : 'false'}>
                    <span className={styles.stepMark} aria-hidden="true">{met ? <Icon meaning="confirmed" size="inline" /> : null}</span>
                    <span>{t(stepLabelKey[step])}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      )}
    </FieldShell>
  );
}
